<script setup lang="ts">
import { Link, router, usePage } from '@inertiajs/vue3';
import {
    CreditCard,
    Flag,
    GraduationCap,
    Lock,
    LogOut,
    Monitor,
    Settings,
    ShieldCheck,
} from 'lucide-vue-next';
import { computed } from 'vue';
import {
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import UserInfo from '@/components/UserInfo.vue';
import { useSentryFeedback } from '@/composables/useSentryFeedback';
import { useTour } from '@/composables/useTour';
import { logout } from '@/routes';
import * as billingRoutes from '@/routes/billing';
import { edit as editProfile } from '@/routes/profile';
import { edit as editSecurity } from '@/routes/security';
import type { User } from '@/types';

type Props = {
    user: User;
};

const page = usePage();
const isAdmin = computed(() => page.props.auth.isAdmin);

const { openFeedbackForm } = useSentryFeedback();
const { startTour, resetTutorial } = useTour();

async function restartTutorial() {
    await resetTutorial();
    startTour();
}

const handleLogout = () => {
    router.flushAll();
};

defineProps<Props>();
</script>

<template>
    <DropdownMenuLabel class="p-0 font-normal">
        <div class="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
            <UserInfo :user="user" :show-email="true" />
        </div>
    </DropdownMenuLabel>
    <DropdownMenuSeparator />
    <DropdownMenuGroup>
        <DropdownMenuItem :as-child="true">
            <Link
                class="block w-full cursor-pointer"
                :href="editProfile()"
                prefetch
            >
                <Settings class="mr-2 h-4 w-4" />
                Mon profil
            </Link>
        </DropdownMenuItem>
        <DropdownMenuItem :as-child="true">
            <Link
                class="block w-full cursor-pointer"
                :href="editSecurity()"
                prefetch
            >
                <Lock class="mr-2 h-4 w-4" />
                Sécurité
            </Link>
        </DropdownMenuItem>
        <DropdownMenuItem :as-child="true">
            <Link
                class="block w-full cursor-pointer"
                :href="billingRoutes.index().url"
                prefetch
            >
                <CreditCard class="mr-2 h-4 w-4" />
                Abonnement
            </Link>
        </DropdownMenuItem>
    </DropdownMenuGroup>
    <DropdownMenuSeparator v-if="isAdmin" />
    <DropdownMenuGroup v-if="isAdmin">
        <DropdownMenuItem :as-child="true">
            <a href="/admin" class="flex w-full cursor-pointer items-center">
                <ShieldCheck class="mr-2 h-4 w-4" />
                Administration
            </a>
        </DropdownMenuItem>
        <DropdownMenuItem :as-child="true">
            <a
                href="/pulse"
                target="_blank"
                rel="noopener"
                class="flex w-full cursor-pointer items-center"
            >
                <Monitor class="mr-2 h-4 w-4" />
                Monitoring
            </a>
        </DropdownMenuItem>
    </DropdownMenuGroup>
    <DropdownMenuSeparator />
    <DropdownMenuGroup>
        <DropdownMenuItem @click="restartTutorial">
            <GraduationCap class="mr-2 h-4 w-4" />
            Relancer le tutoriel
        </DropdownMenuItem>
        <DropdownMenuItem @click="openFeedbackForm">
            <Flag class="mr-2 h-4 w-4" />
            Signaler un problème
        </DropdownMenuItem>
    </DropdownMenuGroup>
    <DropdownMenuSeparator />
    <DropdownMenuItem :as-child="true">
        <Link
            class="block w-full cursor-pointer"
            :href="logout()"
            @click="handleLogout"
            as="button"
            data-test="logout-button"
        >
            <LogOut class="mr-2 h-4 w-4" />
            Se déconnecter
        </Link>
    </DropdownMenuItem>
</template>
